import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { MmRegister } from "../api/mentalModels";

const STORAGE_KEY = "mm_register";

export function getStoredRegister(): MmRegister {
  const v = localStorage.getItem(STORAGE_KEY);
  return v === "plain" || v === "expert" ? v : "guided";
}

const CHANGE_EVENT = "mm-register-change";

export function storeRegister(register: MmRegister) {
  localStorage.setItem(STORAGE_KEY, register);
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

/**
 * The person's chosen voice, shared by every place that shows it: changing it
 * on the model page or in the Today flow updates the other (and other tabs).
 */
export function useMmRegister(): [MmRegister, (r: MmRegister) => void] {
  const [register, setRegister] = useState<MmRegister>(getStoredRegister);
  useEffect(() => {
    const sync = () => setRegister(getStoredRegister());
    window.addEventListener(CHANGE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(CHANGE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);
  const change = useCallback((r: MmRegister) => {
    setRegister(r);
    storeRegister(r);
  }, []);
  return [register, change];
}

/**
 * Register-aware text resolution for the Mental Model Engine.
 * Keys are flat: `mm_<key>__<register>` with fallback chain
 * register → guided → provided default. Register is a second dimension of the
 * string catalog alongside language (design §7).
 */
export function useMmText(register: MmRegister) {
  const { t, i18n } = useTranslation();

  const tm = useCallback(
    (key: string, fallback: string): string => {
      const exact = `mm_${key}__${register}`;
      if (i18n.exists(exact)) return t(exact);
      const guided = `mm_${key}__guided`;
      if (i18n.exists(guided)) return t(guided);
      return t(`mm_${key}`, fallback);
    },
    [t, i18n, register]
  );

  // Object-type-aware variant (ICDF): tries the type-specific phrasing first —
  // `mm_<key>__<register>__<type>` → `mm_<key>__guided__<type>` — then falls
  // back to the plain register chain. Lets rating scales speak the object's
  // language (a goal's certainty ≠ a belief's certainty).
  const tmt = useCallback(
    (key: string, objectType: string | undefined, fallback: string): string => {
      if (objectType) {
        const typed = `mm_${key}__${register}__${objectType}`;
        if (i18n.exists(typed)) return t(typed);
        const typedGuided = `mm_${key}__guided__${objectType}`;
        if (i18n.exists(typedGuided)) return t(typedGuided);
      }
      return tm(key, fallback);
    },
    [t, i18n, register, tm]
  );

  return { tm, tmt, t };
}
